import React, { Component } from 'react';
import PropTypes from 'prop-types';
import fileExtension from 'file-extension';

import { showModal } from '../actions/modal';

import { trackEvent } from '../services/events-tracker-service';
import { canUser } from '../services/permissions-service';
import { getFileIconElement, copyFileUrlToClipboard } from '../services/file-service';
import { convertBytesToHumanReadable, createSlug, getCleanIdFromUrl } from '../utils';
import { isFileImage } from '../utils/file';
import { getCurrentUserId } from '../services/store-service';

import IconGoogleDocument from '../icons/icon-google-document';
import IconGoogleSpreadsheets from '../icons/icon-google-spreadsheets';
import IconGooglePresentation from '../icons/icon-google-presentation';

export default class FileCore extends Component {
    static contextTypes = {
        router: PropTypes.object
    }

    static defaultProps = {
        association: 'Files',
        filePreviewPath: '/files',
        withDownload: true,
        withComment: true,
        withUtils: true
    }

    constructor(props) {
        super(props);

        this.state = {
            thumbnailError: false,
            imageError: false
        };
    }

    handleDropdownCollapse = () => {
        this.setState({
            dropdownVisible: false
        });
    }

    handleDropdownExpand = () => {
        this.setState({
            dropdownVisible: true
        });
    }

    handleUtilClick = ({payload}) => {
        switch (payload.action) {
            case 'copyUrl':
                trackEvent('FILE.URL_COPY', {
                    via: this.props.association
                });
                this.copyUrl();
                break;

            case 'delete':
                this.props.onDelete && this.props.onDelete(this.props.id, this.props.name);
                break;

            case 'share':
                showModal('SHARE_FILE_MODAL', {
                    fileId: this.props.id
                });
                break;

            case 'archive':
                this.props.onArchive && this.props.onArchive(this.props.id);
                break;

            case 'unarchive':
                this.props.onUnarchive && this.props.onUnarchive(this.props.id);
                break;

            default:
                return null;
        }
    }

    handleDownload = () => {
        trackEvent('FILE.DOWNLOAD', {
            via: this.props.association
        });
    }

    handleImageLoad = () => {
        this.setState({
            imageError: false
        });
    }

    handleImageError = () => {
        if (this.state.thumbnailError) {
            this.setState({
                imageError: true
            });

            return;
        }

        this.setState({
            thumbnailError: true
        });
    }

    handleClick = () => {
        this.openFilePreview();
    }

    handleCommentsClick = () => {
        this.openFilePreview(true);
    }

    openFilePreview(openComments = false) {
        if (this.props.noRedirect) {
            showModal('FILE_PREVIEW', {
                fileId: this.props.id
            });

            return;
        }

        if (this.context.router.params && getCleanIdFromUrl(this.context.router.params.fileId) === this.props.id) {
            return;
        }

        this.context.router.push({
            pathname: this.filePreviewPath,
            state: {
                filePreviewPath: this.props.filePreviewPath,
                closeUrl: this.context.router.getCurrentLocation().pathname,
                association: this.props.association,
                siblings: this.props.siblings,
                openComments
            }
        });
    }

    get googleIconElement() {
        switch (this.props.google_type) {
            case 'document':
                return <IconGoogleDocument size="48px" />;

            case 'spreadsheet':
                return <IconGoogleSpreadsheets size="48px" />;

            case 'presentation':
                return <IconGooglePresentation size="48px" />;

            default:
                return null;
        }
    }


    get filePreviewElement() {
        if (this.state.imageError) {
            return this.errorElement;
        }

        if (this.isImage) {
            return this.previewElement;
        }

        if (this.props.google_type) {
            return this.googleIconElement;
        }

        return this.iconElement;
    }

    get thumbnailUrl() {
        return this.state.thumbnailError ? (this.props.url || this.props.download) : this.props.thumbnail;
    }

    get utilItems() {
        const items = [];

        items.push({
            text: 'Share',
            payload: {
                action: 'share'
            }
        });

        items.push({
            text: 'Copy URL',
            payload: {
                action: 'copyUrl'
            }
        });

        if ((this.props.selfEditOnly && this.props.uploader === getCurrentUserId()) || (!this.props.selfEditOnly && canUser('removeFile', [this.props.uploader]))) {
            if (this.props.onDelete) {
                items.push({
                    text: 'Delete',
                    payload: {
                        action: 'delete'
                    },
                    prompt: {
                        title: 'DELETE FILE',
                        text: 'Do you really want to delete this file?',
                        confirmText: 'Delete',
                        confirmClassName: 'btn-caution'
                    }
                });
            }
        }

        return items;
    }

    get nameForType() {
        return (this.props.url && this.props.url.indexOf('.') < 0) && this.props.name ? this.props.name : this.props.url;
    }

    get filePreviewPath() {
        return `${this.props.filePreviewPath}/${this.readableFilePath}`;
    }

    get readableFilePath() {
        return `${this.props.id}-${createSlug(this.props.name)}`;
    }

    get isImage() {
        return isFileImage(this.nameForType);
    }

    get fileIconElement() {
        return getFileIconElement(this.nameForType);
    }

    get fileSize() {
        return convertBytesToHumanReadable(this.props.size);
    }

    get fileType() {
        return fileExtension(this.nameForType);
    }

    copyUrl() {
        copyFileUrlToClipboard(this.props.id);
    }
}
